import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FaEnvelope, FaLock, FaEye, FaEyeSlash, FaWhatsapp } from "react-icons/fa";
import { postLoginbusiness } from "../api/customer.api";
import { ASSETS } from "../config/assets";
import {
  extractTokenFromLoginResponse,
  getPostLoginPath,
  persistLoginSession,
} from "../utils/auth";

const TAGLINE = "One powerful platform to manage conversations, leads, customers, and business growth";

const Login = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ email: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");
    const email = formData.email.trim();

    try {
      const res = await postLoginbusiness({
        email,
        password: formData.password,
      });

      if (res?.success === false) {
        throw new Error(res.message || "Login failed");
      }

      const token = extractTokenFromLoginResponse(res);
      if (!token) {
        throw new Error(res?.message || "Login failed — no token returned");
      }

      persistLoginSession(res);
      navigate(getPostLoginPath());
    } catch (err) {
      setError(err.message || "Invalid email or password");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="login-shell">
      <aside className="login-hero-panel">
        <img src={ASSETS.loginBg} alt="" className="login-hero-photo" />
        <div className="login-hero-content">
          <p className="login-hero-kicker">WhatsApp Business API</p>
          <h2 className="login-hero-title">Conversations that convert</h2>
          <p className="login-hero-tagline">{TAGLINE}</p>
          <ul className="login-hero-points">
            <li>Team inbox and live chat</li>
            <li>Campaigns, templates, and flows</li>
            <li>Analytics you can act on</li>
          </ul>
        </div>
      </aside>

      <main className="login-form-panel">
        <div className="login-card">
          <div className="login-card-brand">
            <img
              src={ASSETS.brandLogo}
              alt={ASSETS.brandName}
              className="login-card-logo"
            />
            <p className="login-product">
              <span className="login-wa-dot" aria-hidden>
                <FaWhatsapp />
              </span>
              WhatsApp Business API
            </p>
          </div>

          <header className="login-card-intro">
            <h1 className="login-title">Sign in</h1>
            <p className="login-subtitle">
              Welcome back. Use your work email to continue.
            </p>
          </header>

          {error && (
            <p className="login-error" role="alert">
              {error}
            </p>
          )}

          <form onSubmit={handleSubmit} className="login-form">
            <label className="login-label" htmlFor="login-email">
              Email
            </label>
            <div className="login-field">
              <FaEnvelope className="login-field-icon" aria-hidden="true" />
              <input
                id="login-email"
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="name@company.com"
                className="login-input"
                required
                autoComplete="email"
              />
            </div>

            <div className="login-label-row">
              <label className="login-label" htmlFor="login-password">
                Password
              </label>
              <Link to="/forgot-password" className="login-link-inline">
                Forgot password?
              </Link>
            </div>
            <div className="login-field">
              <FaLock className="login-field-icon" aria-hidden="true" />
              <input
                id="login-password"
                type={showPassword ? "text" : "password"}
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                placeholder="Enter your password"
                className="login-input"
                required
                autoComplete="current-password"
              />
              <button
                type="button"
                className="login-password-toggle"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>

            <button type="submit" className="login-submit" disabled={isLoading}>
              {isLoading ? (
                <>
                  <span className="spinner" />
                  Signing in…
                </>
              ) : (
                "Sign in"
              )}
            </button>
          </form>

          <p className="login-footer">
            Don&apos;t have an account?{" "}
            <Link to="/register" className="login-link">
              Create Account
            </Link>
          </p>
          <nav className="login-footer-links">
            <Link to="/" className="login-footer-meta">
              Home
            </Link>
            <span aria-hidden="true">·</span>
            <Link to="/service-detail" className="login-footer-meta">
              Service details
            </Link>
          </nav>
        </div>
      </main>
    </div>
  );
};

export default Login;
